/**
 * Helper: Bloomreach
 * -----------------------------------------------------------------------------
 * Bloomreach helper utility functions.
 *
 */
/* eslint-disable no-underscore-dangle */

/**
 * Call the Bloomreach `.track()` event.
 * - https://documentation.bloomreach.com/engagement/docs/tracking#track
 * @param {String} eventName - Name of event.
 * @param {Object} eventProperties - Properties to send to Bloomreach.
 */
export function trackBloomreachEvent(eventName, eventProperties) {
  if (!window.exponea) {
    return
  }

  window.exponea.track(eventName, eventProperties)
}

/**
 * Format a product/variant to match Bloomreach's data structure.
 * @param {Object} product - Product object.
 * @param {Boolean|Object} selectedVariant - Selected variant object.
 * @returns {Object}
 */
export function formatProductForBloomreachreach(product, selectedVariant = false) {
  let variantProperties = {}

  if (selectedVariant) {
    const selectedOptions = selectedVariant.selectedOptions.map((option) => {
      return `${option.name}: ${option.value}`
    })

    variantProperties = {
      variant_id: selectedVariant.id,
      stock_level: selectedVariant.inventory,
      variant_original_price: selectedVariant.compareAtPrice?.amount,
      variant_price: selectedVariant.price?.amount,
      _selected_options: selectedOptions.join(','),
    }
  }

  return {
    active: product.available,
    body_html: product.description,
    brand: product.vendor,
    image: product.featuredImage?.url,
    item_id: product.id,
    original_price: product.compareAtPrice?.amount,
    price: product.price.amount,
    product_id: product.id,
    title: product.title,
    tags: product.tags,
    url: product.url,
    ...variantProperties,
    _handle: product.handle,
    _product_type: product.type,
    _vendor: product.vendor,
  }
}

/**
 * Format cart data to match Bloomreach's data structure
 * @param {Object} cart - Selected cart properties.
 * @returns {Object}
 */
export function formatCartForBloomreach(cart) {
  if (!cart || !cart.items) {
    return {}
  }

  const productIds = []
  const skuIds = []
  let totalQuantity = 0
  const variantIds = []

  cart.items.forEach((item) => {
    productIds.push(item.id)
    skuIds.push(item.sku)
    totalQuantity += item.quantity
    variantIds.push(item.variant_id)
  })

  return {
    product_ids: productIds,
    sku_ids: skuIds,
    total_price: cart.total_price,
    total_quantity: totalQuantity,
    variant_ids: variantIds,
  }
}

/**
 * Format Bloomreach product so it matches Shopify product structure.
 * @param {Object} product - Bloomreach product object.
 * @returns {Object}
 */
export function formatBloomreachProduct(product) {
  let featuredImage
  let media
  let url = product.url

  if (product.image) {
    featuredImage = {
      alt: '',
      url: product.image,
    }
  }

  if (product._images_src_list?.length > 0) {
    media = product._images_src_list.map((src) => {
      return {
        alt: '',
        url: src,
      }
    })
  }

  /**
   * Bloomreach always returns myshopfify URL, update to use custom domain.
   */
  url = url.replace(cnvs.store.permanentDomain, cnvs.store.shopUrl)

  return {
    available: product.active,
    compareAtPrice: {
      amount: product.original_price * 100,
    },
    featuredImage,
    id: product.product_id,
    media,
    price: {
      amount: product.price * 100,
    },
    title: product.title,
    url,
    vendor: product.brand,
  }
}

/**
 * Request recommended products from Bloomreach's API.
 * - https://documentation.bloomreach.com/engagement/docs/personalization#getrecommendation
 * @param {Object} options - Bloomreach options.
 * @returns
 */
export async function getBloomreachRecommendations(options) {
  if (!window.exponea) {
    return
  }

  try {
    await window.exponea.getRecommendation(options)
  } catch (error) {
    reportError('Failed to query Bloomreach for products', error)
  }
}
